/**
 * Public unsubscribe landing — links from every marketing email land here.
 * One click opt-out, with a "resubscribe" button if the user changes their mind.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';

// Public endpoints — no auth needed
const baseURL = process.env.REACT_APP_API_URL || '/api';

export default function UnsubscribePage() {
  const [search] = useSearchParams();
  const token = search.get('token');
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!token) { setState({ loading: false, error: 'Missing unsubscribe token.' }); return; }
    axios.get(`${baseURL}/email/unsubscribe/${encodeURIComponent(token)}`)
      .then(res => setState({ loading: false, ...res.data }))
      .catch(err => setState({ loading: false, error: err.response?.data?.error || 'Invalid link.' }));
  }, [token]);

  const unsubscribe = async () => {
    try {
      const { data } = await axios.post(`${baseURL}/email/unsubscribe`, { token });
      setState(s => ({ ...s, alreadyUnsubscribed: true, message: data.message }));
    } catch (err) {
      setState(s => ({ ...s, error: err.response?.data?.error || 'Could not unsubscribe.' }));
    }
  };

  const resubscribe = async () => {
    try {
      const { data } = await axios.post(`${baseURL}/email/resubscribe`, { token });
      setState(s => ({ ...s, alreadyUnsubscribed: false, message: data.message, resubscribed: true }));
    } catch (err) {
      setState(s => ({ ...s, error: err.response?.data?.error || 'Could not re-subscribe.' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <main id="main-content" tabIndex={-1} className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-10 text-center">
          <div className="text-5xl mb-4">✉️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Email preferences</h1>

          {state.loading && <p className="text-gray-500">Loading…</p>}

          {state.error && (
            <p className="text-red-500 text-sm mt-4">{state.error}</p>
          )}

          {!state.loading && !state.error && (
            <>
              <p className="text-sm text-gray-500 mb-6">{state.email}</p>

              {state.alreadyUnsubscribed ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-sm text-blue-800">
                    You're unsubscribed from Aloha Amino marketing emails.
                    {state.resubscribed && <div className="mt-1 font-semibold">Just kidding — you're back in!</div>}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Changed your mind?</p>
                  <button onClick={resubscribe}
                    className="text-sm font-bold px-6 py-2.5 rounded-lg bg-tea-600 text-white hover:bg-tea-700">
                    Re-subscribe me
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-6">
                    We're sorry to see you go. You'll still get essential account emails (orders, shipping, password resets).
                  </p>
                  <button onClick={unsubscribe}
                    className="text-sm font-bold px-6 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600">
                    Unsubscribe me
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
